/* eslint-disable import/no-duplicates */
import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Empty, List, Col, Row, Badge } from 'antd';
import { Chart, Tooltip, Legend, Point, Line, Interval } from 'bizcharts';
import _ from 'lodash';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { enUS, es, ptBR } from 'date-fns/locale';
import { format } from 'date-fns';
import SessionContext from '@providers/Session/SessionContext';
import CustomCard from '@components/CustomCard';
import { ApplicationsConstants } from '@utils/applicationsConstants';

interface IScale {
  base: { min: number; max: number };
  used: { min: number; max: number };
}

const Usage = () => {
  const { t } = useTranslation('dashboard');
  const { theme } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const locale = keycloak?.idTokenParsed?.locale;

  const { isMot, selectedApp } = useContext(SessionContext);

  const [scale, setScale] = useState<IScale | undefined>(undefined);
  const [chartIns, setChartIns] = useState(null);

  const handleLocale = useCallback(() => {
    if (locale === 'es') {
      return es;
    }
    if (locale === 'en') {
      return enUS;
    }
    return ptBR;
  }, [locale]);

  const colors = [theme?.colors?.blue, theme?.colors?.primary];

  const product = useMemo(() => {
    if (isMot) {
      return 'MOT';
    }
    if (selectedApp === ApplicationsConstants.MULTI_CONNECT) {
      return 'MULTICONNECT';
    }

    return selectedApp?.toUpperCase();
  }, [isMot, selectedApp]);

  const { data: dataBaseUse } = useSwr(
    `/service-proxy/inventory/${product}/dashboards/base-use`,
  );

  const data = useMemo(() => {
    if (!dataBaseUse || dataBaseUse?.length === 0) {
      return [];
    }

    const newDataUse = dataBaseUse.map((item: any) => ({
      ...item,
      base: item.nuDispositivosTotal,
      used: item.nuDispositivosUso,
      monthYear: `${item.month < 10 ? '0' : ''}${item.month}/${item.year}`,
    }));

    newDataUse.reverse();

    const { base: max } = newDataUse.reduce((prev: any, current: any) => {
      return prev.base > current.base ? prev : current;
    });

    let defaultMax;
    if (max > 1000) {
      defaultMax = max + 1000;
    } else if (max > 100) {
      defaultMax = max + 100;
    } else {
      defaultMax = max + 50;
    }

    // @ts-ignore
    setScale({
      base: {
        min: 0,
        max: defaultMax,
      },
      used: {
        min: 0,
        max: defaultMax,
      },
    });

    return newDataUse;
  }, [dataBaseUse]);

  const dataSorted = useMemo(() => {
    const sorted = [...data];
    return sorted
      .sort((a, b) => (a.used < b.used ? 1 : -1))
      .map(item => {
        return {
          ...item,
          date: _.capitalize(
            format(new Date(item.year, item.month - 1, 1), 'MMMM yyyy', {
              locale: handleLocale(),
            }),
          ),
        };
      });
  }, [data, handleLocale]);

  useEffect(() => {
    const handleResize = () => {
      if (chartIns) {
        // @ts-ignore
        chartIns?.forceFit();
      }
    };

    const debounce = _.debounce(handleResize, 300);

    window.addEventListener('resize', debounce);

    return () => window.removeEventListener('resize', debounce);
  }, [chartIns]);

  const renderListItem = useCallback(
    (item: any, index: any) => (
      <List.Item>
        <div>
          <Badge
            className="site-badge-count-109"
            count={index + 1}
            style={{
              backgroundColor:
                index > 2 ? theme?.colors?.background1 : theme?.colors?.primary,
              color: index > 2 ? theme?.colors?.text1 : theme?.colors?.white,
              marginRight: 10,
            }}
          />
          {item.date}
        </div>
        {item.used}
      </List.Item>
    ),
    [theme],
  );

  const onChangeLegend = useCallback(
    (ev: any) => {
      const { item } = ev;
      const { value } = item;
      const checked = !item.unchecked;
      // @ts-ignore
      const geoms = chartIns.geometries;

      geoms.forEach((geom: any) => {
        if (geom.getYScale().field === value) {
          if (checked) {
            geom.show();
          } else {
            geom.hide();
          }
        }
      });
    },
    [chartIns],
  );

  return (
    <CustomCard loading={!dataBaseUse} title={t('usage.title')}>
      {data?.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Row gutter={[48, 16]}>
          <Col lg={12} md={24} style={{ marginBottom: 15 }} xl={16} xs={24}>
            <div>
              <Chart
                data={data}
                height={400}
                scale={scale}
                onGetG2Instance={setChartIns}
                autoFit
              >
                <Legend
                  items={[
                    {
                      value: 'used',
                      name: t('usage.used'),
                      marker: {
                        symbol: 'square',
                        style: { fill: colors[0], r: 5 },
                      },
                    },
                    {
                      value: 'base',
                      name: t('usage.base'),
                      marker: {
                        symbol: 'hyphen',
                        style: { stroke: colors[1], r: 5, lineWidth: 3 },
                      },
                    },
                  ]}
                  allowAllCanceled
                  custom
                  onChange={onChangeLegend}
                />
                <Tooltip
                  itemTpl={
                    '<li style="margin-bottom:8px;" data-index={index}>' +
                    '<span style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>' +
                    '{value}' +
                    '</li>'
                  }
                  shared
                />
                <Interval color={colors[0]} position="monthYear*used" />
                <Line
                  color={colors[1]}
                  position="monthYear*base"
                  shape="smooth"
                  size={3}
                />
                <Point
                  color={colors[1]}
                  position="monthYear*base"
                  shape="circle"
                  size={3}
                />
              </Chart>
            </div>
          </Col>
          <Col lg={12} md={24} xl={8} xs={24}>
            <h3 style={{ marginBottom: 30 }}>{t('usage.top-months')}</h3>
            <List
              dataSource={dataSorted.slice(0, 5)}
              itemLayout="horizontal"
              renderItem={renderListItem}
            />
          </Col>
        </Row>
      )}
    </CustomCard>
  );
};

export default Usage;
